import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import ClassicCard from "../components/ClassicCard";
import Seo from "../components/Seo";
import { X, SlidersHorizontal } from "lucide-react";

export default function Classics() {
  const { lang, t } = useI18n();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ designers: [], brands: [], categories: [] });
  const [active, setActive] = useState({ category: "", designer: "", brand: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const f = await api.get("/classics/filters");
        setFilters(f.data);
      } catch (e) {
        console.error("filters load error", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (active.category) params.category = active.category;
        if (active.designer) params.designer = active.designer;
        if (active.brand) params.brand = active.brand;
        const r = await api.get("/classics", { params });
        setItems(r.data.items || []);
      } catch (e) {
        console.error("classics load error", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [active.category, active.designer, active.brand]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (active.category) chips.push({ key: "category", label: t.categories[active.category] || active.category });
    if (active.designer) chips.push({ key: "designer", label: active.designer });
    if (active.brand) chips.push({ key: "brand", label: active.brand });
    return chips;
  }, [active, t.categories]);

  const hasFilters = activeChips.length > 0;

  return (
    <div data-testid="page-classics" className="stoel-container pt-12 md:pt-20 pb-20">
      <Seo
        path="/klassiekers"
        titleNl="Klassiekers"
        titleEn="Classics"
        descNl="Een doorlopend bijgehouden archief van designontwerpen die de tijd doorstaan — stoelen, verlichting, tafels en banken."
        descEn="A living archive of design pieces that outlast trends — chairs, lighting, tables and sofas."
      />
      <header className="mb-12 md:mb-16 max-w-3xl">
        <div className="stoel-eyebrow mb-4">— {t.nav.classics}</div>
        <h1 className="stoel-display text-5xl md:text-6xl lg:text-7xl">{t.classics.title}</h1>
        <p className="mt-6 text-lg text-stoel-inkSoft leading-relaxed max-w-xl">
          {t.classics.subtitle}
        </p>
      </header>

      {/* Filters */}
      <div className="mb-8 grid md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-1 hidden md:flex items-center gap-2 text-stoel-inkSoft pb-2">
          <SlidersHorizontal size={16} />
          <span className="text-xs uppercase tracking-[0.2em]">{t.common.filters}</span>
        </div>

        <FilterSelect
          testid="filter-category"
          label={t.common.category}
          value={active.category}
          onChange={(v) => setActive({ ...active, category: v })}
          options={filters.categories.map((c) => ({ value: c, label: t.categories[c] || c }))}
          allLabel={t.common.all}
          className="md:col-span-3"
        />
        <FilterSelect
          testid="filter-designer"
          label={t.common.designer}
          value={active.designer}
          onChange={(v) => setActive({ ...active, designer: v })}
          options={filters.designers.map((c) => ({ value: c, label: c }))}
          allLabel={t.common.all}
          className="md:col-span-4"
        />
        <FilterSelect
          testid="filter-brand"
          label={t.common.brand}
          value={active.brand}
          onChange={(v) => setActive({ ...active, brand: v })}
          options={filters.brands.map((c) => ({ value: c, label: c }))}
          allLabel={t.common.all}
          className="md:col-span-4"
        />
      </div>

      {/* Active filter chips */}
      <div className="flex flex-wrap items-center gap-3 mb-6 min-h-[28px]">
        {hasFilters && (
          <span className="text-xs uppercase tracking-[0.2em] text-stoel-walnut">
            {t.classics.activeFilters}:
          </span>
        )}
        {activeChips.map((chip) => (
          <button
            key={chip.key}
            data-testid={`chip-${chip.key}`}
            onClick={() => setActive({ ...active, [chip.key]: "" })}
            className="group inline-flex items-center gap-2 px-3 py-1.5 bg-stoel-paperDeep border border-stoel-line text-xs tracking-wide hover:bg-stoel-ink hover:text-stoel-paper transition-colors"
          >
            {chip.label}
            <X size={12} className="opacity-60 group-hover:opacity-100" />
          </button>
        ))}
        {hasFilters && (
          <button
            data-testid="filter-clear-all"
            onClick={() => setActive({ category: "", designer: "", brand: "" })}
            className="text-xs uppercase tracking-[0.2em] text-stoel-terracotta hover:underline ml-1"
          >
            {t.common.clearAll}
          </button>
        )}
        <span
          data-testid="results-count"
          className="ml-auto text-xs uppercase tracking-[0.2em] text-stoel-inkSoft tabular-nums"
        >
          {items.length} {items.length === 1 ? t.common.result : t.common.results}
        </span>
      </div>

      <div className="stoel-divider mb-10" />

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/5] bg-stoel-paperDeep" />
              <div className="h-4 w-2/3 bg-stoel-paperDeep mt-5" />
              <div className="h-3 w-1/2 bg-stoel-paperDeep mt-2" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div data-testid="empty-state" className="py-20 text-center text-stoel-inkSoft">
          {t.classics.noResults}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {items.map((c, i) => (
            <ClassicCard key={c.id} item={c} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, allLabel, className, testid }) {
  return (
    <label className={`block ${className || ""}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-stoel-walnut mb-2">
        {label}
      </div>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-stoel-paper border-b border-stoel-ink text-stoel-ink py-2.5 pr-8 pl-0 text-base appearance-none focus:outline-none focus:border-stoel-terracotta cursor-pointer"
        >
          <option value="">{allLabel}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-stoel-ink">▾</span>
      </div>
    </label>
  );
}
